import clsx from 'clsx';
import { CRUISE_DATES_FILTER } from 'components/CruiseSearchWidget/constants';
import { CalendarType } from 'components/CruiseSearchWidget/types';
import { useFiltersContext } from 'components/FiltersProvider/FiltersProvider';
import { CaretIcon } from 'components/Icon';
import { differenceInMonths, format } from 'date-fns';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { DateRangeFilter } from 'lib/apiClient';
import { UpdateSelectionItem } from 'lib/mappers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './DatesCalendar.css';
import { DatesCalendarType, DayType, MonthType } from './DatesCalendar.types';
import { Month } from './Month';
import {
	DAYS_OF_WEEK,
	MONTHS_GAP_DEFAULT,
	MONTHS_GAP_DESKTOP,
	MONTHS_GAP_TABLET_PORTRAIT,
	VISIBLE_MONTHS_COUNT_DESKTOP,
	VISIBLE_MONTHS_COUNT_TABLET,
} from './constants';
import { formatCalendar, getCalendar, getInitialSelection, getWeekDays, updateCalendarSelection } from './utils/datesCalendarHandler';

interface DatesCalendarProps {
	dateFilter: DateRangeFilter;
}

const DatesCalendar = ({ dateFilter }: DatesCalendarProps) => {
	const {
		datesRangeSel,
		setDatesRangeSel,
		updateSelection,
		filters,
		historicalSelection,
		locale,
		promotionData: { criteriaToSearchSelection },
	} = useFiltersContext();
	const currentMonth = format(new Date(), 'yyyy-MM');
	const daysOfTheWeek = getWeekDays(locale.language) || DAYS_OF_WEEK;
	const startDate = dateFilter.startDate.toISOString().split('T')[0];
	const endDate = dateFilter.endDate.toISOString().split('T')[0];
	const carouselRef = useRef<HTMLDivElement>(null);
	const monthRef = useRef<HTMLDivElement>(null);
	const [initialScrollPerformed, setInitialScrollPerformed] = useState(false);
	const [lastIndexVisibleMonth, setLastIndexVisibleMonth] = useState(1);
	const { isTabletPortrait, isTabletLandscape, isMobile } = useBreakpoint();
	const initialDatesSelection = historicalSelection.find((item) => item.calendarType === CalendarType.Dates)?.option.dateRanges;

	const monthsGap = useMemo(() => {
		if (isMobile || isTabletLandscape) {
			return MONTHS_GAP_DEFAULT;
		}

		if (isTabletPortrait) {
			return MONTHS_GAP_TABLET_PORTRAIT;
		}

		return MONTHS_GAP_DESKTOP;
	}, [isMobile, isTabletLandscape, isTabletPortrait]);

	const visibleMonths = useMemo(() => {
		if (isTabletLandscape || isTabletPortrait) {
			return VISIBLE_MONTHS_COUNT_TABLET;
		}

		return VISIBLE_MONTHS_COUNT_DESKTOP;
	}, [isTabletLandscape, isTabletPortrait]);

	const cruiseDatesFilter = useMemo(() => {
		return filters.find((filter) => filter.key === CRUISE_DATES_FILTER.key) as DateRangeFilter;
	}, [filters]);

	const defaultCalendar: DatesCalendarType = useMemo(
		() =>
			formatCalendar(
				getCalendar(
					startDate,
					endDate,
					cruiseDatesFilter,
					locale.language,
					criteriaToSearchSelection ? criteriaToSearchSelection[CRUISE_DATES_FILTER.key] : [],
				),
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[startDate, endDate, cruiseDatesFilter, filters, criteriaToSearchSelection],
	);

	const [currentIdxMonth, setCurrentIdxMonth] = useState(0);
	const [datesCalendar, setDatesCalendar] = useState<DatesCalendarType>(() => {
		if (!initialDatesSelection?.[0]) return defaultCalendar;
		const { updatedCalendar, startSelectionDate, endSelectionDate } = getInitialSelection(defaultCalendar, initialDatesSelection[0]);
		if (startSelectionDate && endSelectionDate) setDatesRangeSel([startSelectionDate, endSelectionDate]);
		return updatedCalendar;
	});

	const firstEnabledMonth: MonthType | undefined = useMemo(() => {
		return datesCalendar.reduce((acum: MonthType | undefined, year) => {
			if (!acum) {
				acum = year.months.find((month) => !month.isDisabled);
			}

			return acum;
		}, undefined);
	}, [datesCalendar]);

	const showFacetingMonth = useCallback(
		(month: MonthType) => {
			if (firstEnabledMonth) {
				return differenceInMonths(new Date(month.code), new Date(firstEnabledMonth.code)) > 0 || month.code === firstEnabledMonth.code;
			}
			return differenceInMonths(new Date(month.code), new Date(currentMonth)) > 0 || month.code === currentMonth;
		},
		[firstEnabledMonth, currentMonth],
	);

	useEffect(() => {
		if (initialScrollPerformed) {
			return;
		}

		if (!initialDatesSelection && firstEnabledMonth) {
			setInitialScrollPerformed(true);
			return;
		}

		if (datesRangeSel.length > 0 && firstEnabledMonth && carouselRef?.current && visibleMonths) {
			setLastIndexVisibleMonth(carouselRef.current.childElementCount - visibleMonths);
			const startMonthIndex = differenceInMonths(new Date(datesRangeSel[0].code), new Date(firstEnabledMonth?.code));
			const monthElement = carouselRef.current.children.item(startMonthIndex);
			monthElement?.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' });
			setCurrentIdxMonth(startMonthIndex);
			setInitialScrollPerformed(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [firstEnabledMonth, datesRangeSel, carouselRef?.current, initialScrollPerformed, visibleMonths]);

	const onDateRangeChange = (day: DayType) => {
		const [start, end] = datesRangeSel;
		let newRangeSelection = [...datesRangeSel];

		if (!start) newRangeSelection[0] = day;
		else if (!end) newRangeSelection[1] = day;
		else if (start && end) newRangeSelection = [day];

		newRangeSelection.sort((a, b) => new Date(a.code).getTime() - new Date(b.code).getTime());

		const updatedCalendar = updateCalendarSelection(defaultCalendar, newRangeSelection);
		if (newRangeSelection.length === 2) {
			const value: string = [newRangeSelection[0].code, newRangeSelection[1].code].join('~');
			const selection: UpdateSelectionItem = {
				value: [value],
				year: newRangeSelection[0].year,
				calendarType: CalendarType.Dates,
			};
			updateSelection(dateFilter.key, selection);
		}
		setDatesRangeSel(newRangeSelection);
		setDatesCalendar(updatedCalendar);
	};

	useEffect(() => {
		if (datesRangeSel.length === 0) setDatesCalendar(defaultCalendar);
		if (datesRangeSel.length === 2) setDatesCalendar(updateCalendarSelection(defaultCalendar, datesRangeSel));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [datesRangeSel, defaultCalendar]);

	useEffect(() => {
		if (!carouselRef.current || !monthRef.current) return;
		carouselRef.current.style.width = isMobile ? 'unset' : `${monthRef.current.clientWidth * visibleMonths + monthsGap * (visibleMonths - 1)}px`;
	}, [visibleMonths, monthsGap, isMobile, monthRef?.current?.clientWidth]);

	const onNext = () => {
		if (!carouselRef.current || !monthRef.current) return;
		setLastIndexVisibleMonth(carouselRef.current.childElementCount - visibleMonths);

		if (currentIdxMonth < lastIndexVisibleMonth) {
			carouselRef.current.scrollTo({ left: (currentIdxMonth + 1) * (monthRef.current.clientWidth + monthsGap), behavior: 'smooth' });
			setCurrentIdxMonth((curr) => curr + 1);
		} else {
			return;
		}
	};

	const onPrev = () => {
		if (!carouselRef.current || !monthRef.current) return;

		if (currentIdxMonth === 0) {
			return;
		} else {
			carouselRef.current.scrollTo({ left: (currentIdxMonth - 1) * (monthRef.current.clientWidth + monthsGap), behavior: 'smooth' });
			setCurrentIdxMonth((curr) => curr - 1);
		}
	};

	return (
		<div className={styles.root}>
			<button type="button" className={clsx(styles.caretContainer, { [styles.caretDisable]: currentIdxMonth === 0 })} onClick={onPrev}>
				<CaretIcon className={clsx(styles.caretIcon, styles.caretLeft)} />
			</button>
			<div ref={carouselRef} className={styles.carousel}>
				{datesCalendar.map((year) =>
					year.months
						.filter((month) => showFacetingMonth(month))
						.map((month) => (
							<div className={styles.month} ref={monthRef} key={month.code}>
								<Month year={year.year} month={month} weekDays={daysOfTheWeek} onDayClick={onDateRangeChange} />
							</div>
						)),
				)}
			</div>
			<button
				type="button"
				className={clsx(styles.caretContainer, { [styles.caretDisable]: currentIdxMonth === lastIndexVisibleMonth })}
				onClick={onNext}
			>
				<CaretIcon className={clsx(styles.caretIcon, styles.caretRight)} />
			</button>
		</div>
	);
};

export { DatesCalendar };
